import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Send, MessageCircle, Check, CheckCheck } from "lucide-react";
import type { ChatMessage } from "@/types";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerChat = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [bookingName, setBookingName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/${bookingId}/messages?token=${token}`);
      setMessages(res.data.messages);
      setBookingName(res.data.booking_name);
      setError(null);
      await axios.post(`${API}/chat/${bookingId}/mark-read?token=${token}`, { reader_type: "customer" }).catch(() => {});
    } catch {
      if (!messages.length) setError("Unable to load chat. The link may be invalid or expired.");
    }
  }, [bookingId, token, messages.length]);

  useEffect(() => {
    if (bookingId && token) {
      fetchMessages();
      intervalRef.current = setInterval(fetchMessages, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [bookingId, token, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/${bookingId}/customer-send?token=${token}`, { message: newMessage.trim() });
      setNewMessage("");
      fetchMessages();
    } catch (err) {
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="customer-chat-page">
      <header className="bg-white shadow-sm border-b px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_spa-bookings-5/artifacts/syk2guux_Angel-Massage-logo-png.png"
            alt="Angel Massage"
            className="h-8 w-auto"
          />
          <div>
            <h1 className="text-sm font-semibold text-gray-800">Angel Massage Chat</h1>
            <p className="text-xs text-gray-500">{bookingName ? `Booking for ${bookingName}` : "Loading..."}</p>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 max-w-lg mx-auto w-full">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 text-sm mt-8">
            <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No messages yet. Send a message to your therapist!</p>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex mb-3 ${msg.sender_type === "customer" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                msg.sender_type === "customer"
                  ? "bg-pink-500 text-white rounded-br-sm"
                  : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm"
              }`}
              data-testid={`chat-msg-${msg.sender_type}`}
            >
              <p className="text-xs font-medium opacity-70 mb-1">
                {msg.sender_type === "customer" ? "You" : msg.sender_name}
                {msg.sent_via === "line" ? " (LINE)" : ""}
              </p>
              {/* For therapist messages with translation, show translated version first */}
              {msg.sender_type === "therapist" && msg.translated_message ? (
                <>
                  <p className="text-sm whitespace-pre-wrap">{msg.translated_message}</p>
                  <p className="text-xs whitespace-pre-wrap opacity-60 mt-1 italic">{msg.message}</p>
                </>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
              )}
              {msg.sender_type === "customer" && (
                <div className="flex justify-end mt-1">
                  {msg.read_at ? (
                    <CheckCheck className="w-3.5 h-3.5 text-blue-200" data-testid="read-receipt-seen" />
                  ) : (
                    <Check className="w-3.5 h-3.5 text-white/50" data-testid="read-receipt-sent" />
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </main>

      <footer className="bg-white border-t px-4 py-3">
        <div className="max-w-lg mx-auto flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder="Type a message..."
            className="flex-1"
            data-testid="chat-input"
          />
          <Button onClick={handleSend} disabled={sending || !newMessage.trim()} className="bg-pink-500 hover:bg-pink-600" data-testid="chat-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default CustomerChat;
